import { LOCAL_STORAGE_OIDC_TOKEN_NAME } from '@constants';
import axios from 'axios';
// eslint-disable-next-line import/no-unresolved, camelcase
import * as jwt_decode from 'jwt-decode';
import { nanoid } from 'nanoid';
import showSnackbar from './apiSnackbar';
import { showNoticePopup } from './noticePopup';
import developmentDomainHost from './domainHeader';
import { getAppPath, isCurrentAppPath } from './appPath';
import { waitForRetry } from '../../../utils/retryDelay';

const REDIRECT_OUT_OF_APP_ON_500 = false;

// apiClient runs once per named provider and again on every App render, so
// warn about a missing local API host once per page load, not once per client.
let warnedMissingApiHost = false;

const apiClient = (baseUrl, lang, { domainHeader = false } = {}) => {
  const axiosInstance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    headers: {
      common: {
        'x-lang': lang.get(),
        'Accept-Language': lang.get(),
      },
    },
  });

  if (domainHeader && process.env.NODE_ENV === 'development') {
    const apiHost = developmentDomainHost();
    if (apiHost) {
      axiosInstance.defaults.headers.common.domain = apiHost;
    } else if (!warnedMissingApiHost) {
      warnedMissingApiHost = true;
      // eslint-disable-next-line no-console
      console.warn(
        'REACT_APP_SXCL_SERVICES_API_HOST is not set, so API requests will omit the domain header.',
      );
    }
  }

  axiosInstance.interceptors.request.use(config => {
    let sessionState = null;
    const tokenName = window.localStorage.getItem(
      LOCAL_STORAGE_OIDC_TOKEN_NAME,
    );
    const sessionAuthData = window.localStorage.getItem(tokenName) || null;

    const token = sessionAuthData
      ? JSON.parse(sessionAuthData)?.access_token
      : null;

    if (token) {
      try {
        const decodedToken = jwt_decode(token);
        sessionState = decodedToken?.session_state || null;
      } catch {
        sessionState = null;
      }
    }

    const now = new Date().toISOString();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
      ...config,
      params: {
        ...config.params,
        _ts: Date.now(),
      },
      headers: {
        ...config.headers,
        requestid: nanoid(21),
        Authorization: `Bearer ${token}`,
        ...(sessionState && { sessionid: sessionState }),
        'X-Client-Datetime': now,
        'X-Client-Timezone': timezone,
      },
    };
  });

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status;

      if (error?.code === 'ERR_NETWORK' || status === 503) {
        if (error?.config?.suppressGlobalErrorRedirect) {
          return Promise.reject(error);
        }

        const errorMessage = {
          message: encodeURIComponent(
            `Network error when accessing url ${error?.config?.url}`,
          ),
        };
        window.location.href = `/servicedown?networkError=${JSON.stringify(
          errorMessage,
        )}`;
        return error.response;
      }
      if (status === 401) {
        if (
          error?.config?.signal?.aborted ||
          isCurrentAppPath('/unauthorized')
        ) {
          return error.response;
        }
        if (error?.config?.allowTokenRenewal) {
          return waitForRetry(5000, error.config.signal).then(() => {
            if (error.config.signal?.aborted) {
              return Promise.reject(error);
            }
            return axiosInstance.request({
              ...error.config,
              allowTokenRenewal: false,
            });
          });
        }
        window.location.href = getAppPath('/unauthorized');
      }

      if (status === 403) {
        showSnackbar({
          type: 'error',
          errors:
            error?.response?.data?.errors ||
            'You do not have permission to perform this action',
        });
      }
      if (status === 409) {
        if (
          error?.response?.data?.errors &&
          error?.response?.data?.errors.find(
            e => e.errorCode === 'ConflictException',
          )
        ) {
          // No message: the popup renders its localized stale-data default.
          showNoticePopup(null, error?.response?.data?.errors);
        }
      }

      if (REDIRECT_OUT_OF_APP_ON_500 && status === 500) {
        const errorMessage = {
          message: encodeURIComponent(
            `Server error when accessing url ${error?.config?.url}`,
          ),
        };
        window.location.href = `/servererror?serverError=${JSON.stringify(
          errorMessage,
        )}`;
      }

      // 400 bad request return response
      return error.response;
    },
  );

  return axiosInstance;
};

export default apiClient;
